/**
 * Consentimento do OAuth dos MCPs do ERP (ADR-0089 e ADR-0091).
 *
 * A IA (Claude, ChatGPT...) abre esta página com os parâmetros do OAuth. A
 * pessoa logada vê o que vai liberar, na unidade dela, e decide. Para o MCP
 * da Central é só leitura; para o MCP do ERP (`/mcp`) ela marca o que libera
 * (ver a agenda, mexer na agenda, consultar a Central), entre o que pode no
 * ERP. O backend confere o pedido (cliente, redirect registrado, PKCE,
 * recurso) e pede a verificação em duas etapas recente; daqui o navegador
 * volta à IA com o código ou com a recusa.
 */
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import { useAuth } from "../../context/AuthContext";
import { useCarga } from "./comum";
import { Acoes, Aviso, Botao, BotaoPrimario, Pagina, Secao, Titulo, Topo } from "./styles";

const P = "/api/central-projetos/oauth/consentimento";

function paramsDaUrl(search) {
  return Object.fromEntries(new URLSearchParams(search).entries());
}

export default function AutorizarClaude() {
  const { search } = useLocation();
  const { request } = useAuth();
  const [enviando, setEnviando] = useState(false);
  const [erroEnvio, setErroEnvio] = useState("");
  const [marcados, setMarcados] = useState([]);
  const params = paramsDaUrl(search);
  const { dados, erro, carregando } = useCarga(
    (req) => req(`${P}${search}`, { silentError: true }).then((r) => r?.data ?? r),
    [search],
  );
  useEffect(() => { setMarcados((dados?.escopos || []).map((e) => e.codigo)); }, [dados]);
  const doErp = dados?.tipo === "erp";
  const alternar = (codigo) => setMarcados((lista) => (lista.includes(codigo) ? lista.filter((c) => c !== codigo) : [...lista, codigo]));

  async function decidir(decisao) {
    setEnviando(true);
    setErroEnvio("");
    try {
      const resposta = await request(P, { method: "POST", body: JSON.stringify({ ...params, decisao, ...(doErp ? { escopos: marcados } : {}) }), silentError: true });
      const destino = (resposta?.data ?? resposta)?.redirecionar;
      if (destino) window.location.assign(destino);
    } catch (e) {
      setErroEnvio(e?.message || "Não foi possível concluir. Tente conectar de novo pela IA.");
      setEnviando(false);
    }
  }

  return (
    <Pagina style={{ maxWidth: 560, margin: "0 auto" }}>
      <Topo>
        <Titulo>
          <h1>{dados ? `Conectar ${dados.aplicativo} ${doErp ? "ao Pharu ERP" : "à Central de Projetos"}` : "Conectar uma IA ao Pharu ERP"}</h1>
          <p>{doErp ? "Em seu nome, na sua unidade, com as mesmas regras da tela." : "Somente leitura, na sua unidade."}</p>
        </Titulo>
      </Topo>
      {carregando ? <Aviso $tom="info">Conferindo o pedido…</Aviso> : null}
      {erro ? <Aviso $tom="danger">{erro.message || "Pedido de conexão inválido."}</Aviso> : null}
      {dados && doErp ? (
        <Secao>
          <p style={{ margin: "0 0 8px", fontSize: 14 }}>
            O <strong>{dados.aplicativo}</strong> vai agir em nome de <strong>{dados.pessoa}</strong> na unidade{" "}
            <strong>{dados.unidade}</strong>. Marque o que ele pode:
          </p>
          <div role="group" aria-label="O que liberar" style={{ display: "flex", flexDirection: "column", gap: 10, margin: "4px 0 12px" }}>
            {dados.escopos.map((e) => (
              <label key={e.codigo} style={{ display: "flex", gap: 10, alignItems: "flex-start", cursor: "pointer" }}>
                <input type="checkbox" checked={marcados.includes(e.codigo)} onChange={() => alternar(e.codigo)} style={{ marginTop: 3 }} />
                <span><strong style={{ fontWeight: 600 }}>{e.rotulo}</strong><br /><span style={{ fontSize: 13, color: "var(--ink-3)" }}>{e.descricao}</span></span>
              </label>
            ))}
          </div>
          <Aviso $tom="info">
            Você vê e corta este acesso quando quiser em Configurações › Integrações de IA.
          </Aviso>
          {erroEnvio ? <Aviso $tom="danger">{erroEnvio}</Aviso> : null}
          <Acoes>
            <BotaoPrimario type="button" disabled={enviando || !marcados.length} onClick={() => decidir("permitir")}>Permitir</BotaoPrimario>
            <Botao type="button" disabled={enviando} onClick={() => decidir("recusar")}>Recusar</Botao>
          </Acoes>
        </Secao>
      ) : null}
      {dados && !doErp ? (
        <Secao>
          <Aviso $tom="info">
            O <strong>{dados.aplicativo}</strong> vai poder consultar, em nome de <strong>{dados.pessoa}</strong>,
            na unidade <strong>{dados.unidade}</strong>: clientes, ambientes e prazos, pendências, decisões,
            divergências e, se você pode ler as conversas, as mensagens do WhatsApp com CPF, RG, senhas e
            códigos de porta mascarados. Não envia mensagem e não muda nada. Cada consulta fica registrada, e o
            acesso pode ser revogado na configuração da Central.
          </Aviso>
          {erroEnvio ? <Aviso $tom="danger">{erroEnvio}</Aviso> : null}
          <Acoes>
            <BotaoPrimario type="button" disabled={enviando} onClick={() => decidir("permitir")}>Permitir</BotaoPrimario>
            <Botao type="button" disabled={enviando} onClick={() => decidir("recusar")}>Recusar</Botao>
          </Acoes>
        </Secao>
      ) : null}
    </Pagina>
  );
}
